import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ouraMutationOptions } from "@/services/queryOptions/ouraQueryOptions";
import { vaultTransferSchema } from "@/types/oura";
import { SecurityNote, TextAreaField, TextField } from "../components/FormControls";
import { OperationsManager } from "./OperationsManager";

const defaults: z.infer<typeof vaultTransferSchema> = {
  payer: "",
  collection: "",
  asset: "",
  project_token_mint: "",
  new_token_mint: "",
  amount: "0",
};

export function VaultManager() {
  const { toast } = useToast();
  const form = useForm<z.infer<typeof vaultTransferSchema>>({
    resolver: zodResolver(vaultTransferSchema),
    defaultValues: defaults,
  });
  const transfer = useMutation(ouraMutationOptions<Record<string, unknown>>("POST", "/api/v1/oura/vault/transfer"));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Vault</h2>
        <p className="text-sm text-zinc-400">Transfer token balances into NFT vault accounts.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Vault Transfer</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 lg:grid-cols-2"
            onSubmit={form.handleSubmit((values) =>
              transfer.mutate(
                {
                  payer: values.payer,
                  collection: values.collection,
                  asset: values.asset,
                  project_token_mint: values.project_token_mint,
                  new_token_mint: values.new_token_mint,
                  amount: values.amount,
                },
                {
                  onSuccess: (result) => {
                    toast({ title: "Vault transfer submitted", description: result.signature });
                    form.reset(defaults);
                  },
                },
              ),
            )}
          >
            <div className="lg:col-span-2">
              <SecurityNote />
            </div>
            <TextAreaField
              label="Payer secretKey JSON"
              placeholder="[12,34,56,...]"
              registration={form.register("payer")}
              error={form.formState.errors.payer}
            />
            <TextField label="Collection" placeholder="So11111111111111111111111111111111111111112" registration={form.register("collection")} error={form.formState.errors.collection} />
            <TextField label="Asset" placeholder="So11111111111111111111111111111111111111112" registration={form.register("asset")} error={form.formState.errors.asset} />
            <TextField
              label="Project token mint"
              placeholder="So11111111111111111111111111111111111111112"
              registration={form.register("project_token_mint")}
              error={form.formState.errors.project_token_mint}
            />
            <TextField label="New token mint" placeholder="So11111111111111111111111111111111111111112" registration={form.register("new_token_mint")} error={form.formState.errors.new_token_mint} />
            <TextField label="Amount" placeholder="1000000" registration={form.register("amount")} error={form.formState.errors.amount} />
            <Button className="lg:col-span-2" disabled={transfer.isPending}>Transfer to vault</Button>
          </form>
        </CardContent>
      </Card>
      <OperationsManager resourceType="vault" compact />
    </div>
  );
}
